import React from 'react';
import styled, { keyframes } from 'styled-components';

/* ── Theme ─────────────────────────────────────────────────── */
const GOLD       = '#C9952A';
const GOLD_LIGHT = '#E8B84B';
const CREAM      = '#FAF6ED';
const WARM_WHITE = '#FFFCF5';
const DARK       = '#1C1208';
const DARK_MID   = '#2A1A0A';
const TEXT       = '#2A1F0E';
const TEXT_MID   = '#6B5540';

/* ── GIF file paths ──────────────────────────────────────────── */
const GIF_IK     = '/demo_AFA2(1).gif';
const GIF_TWIN   = '/3dAvtar(1).gif';
const GIF_GATI   = '/a%20person%20raises%20his%20right%20leg%20steps%20on%20the%20platform%20and%20brings%20his%20left%20foot%20up.gif';

const float = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/* ── Root ───────────────────────────────────────────────────── */
const PageContainer = styled.div`
  background: ${CREAM};
  overflow: hidden;
`;

/* ── Hero ───────────────────────────────────────────────────── */
const Hero = styled.section`
  position: relative;
  min-height: 72vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem 8rem;
  background: linear-gradient(135deg, ${DARK} 0%, ${DARK_MID} 55%, ${DARK} 100%);
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 88%, 0 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 50% -10%, rgba(201,149,42,0.28) 0%, transparent 55%),
      radial-gradient(ellipse at 5%  90%, rgba(201,149,42,0.10) 0%, transparent 40%),
      radial-gradient(ellipse at 95% 90%, rgba(201,149,42,0.10) 0%, transparent 40%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse cx='40' cy='12' rx='8' ry='18' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(0%2C40%2C40)'/%3E%3Cellipse cx='40' cy='12' rx='8' ry='18' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(45%2C40%2C40)'/%3E%3Cellipse cx='40' cy='12' rx='8' ry='18' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(90%2C40%2C40)'/%3E%3Cellipse cx='40' cy='12' rx='8' ry='18' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(135%2C40%2C40)'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  max-width: 860px;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const Sanskrit = styled.h2`
  font-size: 2.2rem;
  color: rgba(232,184,75,0.65);
  margin-bottom: 0.6rem;
  font-family: 'Playfair Display', serif;
  font-weight: 400;
  font-style: italic;
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(3rem, 6vw, 4.8rem);
  font-weight: 900;
  margin-bottom: 0.6rem;
  line-height: 1.05;
  background: linear-gradient(135deg, #fff 0%, ${GOLD_LIGHT} 50%, #fff 100%);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 6s ease infinite, ${float} 7s ease-in-out infinite;
`;

const HeroSub = styled.h3`
  font-size: 1.15rem;
  color: rgba(255,255,255,0.65);
  font-weight: 400;
  font-family: 'Source Serif 4', serif;
  margin-top: 0.8rem;
`;

const GoldRule = styled.div`
  width: 60px;
  height: 1.5px;
  background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
  margin: 1.4rem auto;
  border-radius: 2px;
`;

/* ── Main content — same pattern as Anubhuti / Dhara / Ranjana ─ */
const MainContent = styled.div`
  max-width: 1100px;
  margin: -4rem auto 0;
  padding: 0 2rem 5rem;
  position: relative;
  z-index: 2;
`;

/* ── White card section (matches all other pages) ─────────────── */
const Section = styled.section`
  background: #FFFCF5;
  border-radius: 20px;
  padding: 2.8rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 28px rgba(201,149,42,0.09);
  border: 1px solid rgba(201,149,42,0.12);
`;

const SectionLabel = styled.p`
  font-family: 'Source Serif 4', serif;
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${GOLD};
  font-weight: 600;
  margin-bottom: 0.4rem;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.7rem, 3vw, 2.3rem);
  color: ${TEXT};
  text-align: center;
  margin-bottom: 3.5rem;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 52px;
    height: 2px;
    background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
    border-radius: 2px;
    margin: 0.8rem auto 0;
  }
`;

/* ── Feature card (2-col) ───────────────────────────────────── */
const FeatureGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const FeatureCard = styled.div`
  background: ${WARM_WHITE};
  border-radius: 24px;
  display: grid;
  grid-template-columns: 1fr minmax(260px, 360px);
  gap: 0;
  overflow: hidden;
  box-shadow: 0 10px 36px rgba(201,149,42,0.10);
  border: 1px solid rgba(201,149,42,0.14);
  transition: box-shadow 0.35s ease, transform 0.35s ease;

  /* reverse layout for even cards */
  &:nth-child(even) {
    grid-template-columns: minmax(260px, 360px) 1fr;

    .card-media { order: -1; }
  }

  &:hover {
    box-shadow: 0 20px 52px rgba(201,149,42,0.18);
    transform: translateY(-4px);
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr !important;

    .card-media { order: -1 !important; }
  }
`;

const CardContent = styled.div`
  padding: 3rem 2.6rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CardNum = styled.span`
  display: inline-block;
  font-family: 'Playfair Display', serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${GOLD};
  margin-bottom: 0.8rem;
  background: rgba(201,149,42,0.1);
  padding: 0.25rem 0.75rem;
  border-radius: 30px;
  width: fit-content;
`;

const CardTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${TEXT};
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const CardText = styled.p`
  font-size: 1rem;
  line-height: 1.9;
  color: ${TEXT_MID};
  font-family: 'Source Serif 4', serif;
`;

const CardMedia = styled.div`
  background: linear-gradient(160deg, #F5EDD8 0%, #FAF6ED 100%);
  border-left: 1px solid rgba(201,149,42,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 300px;
  position: relative;

  /* subtle gold corner accent */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 80% 20%, rgba(201,149,42,0.10) 0%, transparent 55%),
      radial-gradient(circle at 20% 80%, rgba(232,184,75,0.07) 0%, transparent 45%);
    pointer-events: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    max-height: 320px;
    padding: 1.5rem;
    position: relative;
    z-index: 1;
    /* drop-shadow to lift the figure off the background */
    filter: drop-shadow(0 8px 20px rgba(0,0,0,0.12));
  }

  /* even cards: border on right instead */
  .card-media & {
    border-left: none;
    border-right: 1px solid rgba(201,149,42,0.12);
  }

  @media (max-width: 860px) {
    min-height: 240px;
    border-left: none;
    border-bottom: 1px solid rgba(201,149,42,0.12);

    img { max-height: 240px; }
  }
`;

/* ── Component ──────────────────────────────────────────────── */
const Abhyas = () => (
  <PageContainer>

    {/* ── HERO ─────────────────────────────────────────────── */}
    <Hero>
      <HeroContent>
        <Sanskrit>अभ्यास</Sanskrit>
        <HeroTitle>Abhyās</HeroTitle>
        <GoldRule />
        <HeroSub>Movement Intelligence Platform</HeroSub>
      </HeroContent>
    </Hero>

    {/* ── FEATURES ─────────────────────────────────────────── */}
    <MainContent>
    <Section>
        <SectionLabel>Platform Capabilities</SectionLabel>
        <SectionTitle>Abhyas Platform</SectionTitle>

        <FeatureGrid>

          {/* 01 — Real-Time IK */}
          <FeatureCard>
            <CardContent>
              <CardNum>01</CardNum>
              <CardTitle>Real-Time Markerless Inverse Kinematics</CardTitle>
              <CardText>
                Custom hardware and software for wireless, real-time tracking of anatomical
                landmarks with simultaneous generation of Inverse Kinematics (IK) on-field.
                We have capabilities to re-engineer the system for a given task and add
                prediction layers as per the requirements on field.
              </CardText>
            </CardContent>
            <CardMedia className="card-media">
              <img src={GIF_IK} alt="Real-time body tracking demo" loading="lazy" />
            </CardMedia>
          </FeatureCard>

          {/* 02 — Digital Twin */}
          <FeatureCard>
            <CardContent>
              <CardNum>02</CardNum>
              <CardTitle>Synchronous Digital Twin</CardTitle>
              <CardText>
                A software avatar that moves in synchronization with the human subject in near
                real time. This Digital Twin replicates posture &amp; inverse kinematic states
                without body-worn sensors. It is under continuous development and will
                increasingly replicate more biomechanical parameters. Invaluable for monitoring
                public spaces while preserving privacy.
              </CardText>
            </CardContent>
            <CardMedia className="card-media">
              <img src={GIF_TWIN} alt="3D avatar digital twin" loading="lazy" />
            </CardMedia>
          </FeatureCard>

          {/* 03 — GatiGPT */}
          <FeatureCard>
            <CardContent>
              <CardNum>03</CardNum>
              <CardTitle>GatiGPT — Text-to-Movement LLM</CardTitle>
              <CardText>
                A Transformer-based text-movement animation model allowing one to prompt for
                something like: <em>"Generate a person who climbs the stool, gets down, and
                returns to place."</em> &amp; generate a moving GIF in response.
              </CardText>
            </CardContent>
            <CardMedia className="card-media">
              <img src={GIF_GATI} alt="GatiGPT text-to-movement demo" loading="lazy" />
            </CardMedia>
          </FeatureCard>

        </FeatureGrid>
    </Section>
    </MainContent>

  </PageContainer>
);

export default Abhyas;
